import { notFound } from 'next/navigation'
import _, { camelCase, capitalize, chunk, join, reverse } from 'lodash'
import deepEqual from 'deep-equal'
import { Metadata } from 'next'
import moment from 'moment'

import { API_ENDPOINT, APP_LANGUAGES } from '@/constants'
import {
  IFee,
  TLang,
  IOption,
  TExtras,
  TCoverage,
  IPageMeta,
  TEquipment,
  TExtraAction,
  IPricedEquip,
  IPricedCoverage,
  IVehicleCharge,
  IBranch,
} from '@/types'
import { DepthKeys } from '@/hooks'
import { getBlogPage } from './request'
import { appPaths, homePath } from './urls'

export const getMessages = async (appLang: TLang) => {
  if (!APP_LANGUAGES.includes(appLang)) return notFound()
  try {
    const response = await fetch(
      `http://localhost:${process.env.PORT}/api/${appLang}`,
      {
        method: 'GET',
        next: {
          revalidate: 0,
        },
        //@ts-ignore
        headers: {
          'X-Api-Key': process.env.TRIGGER_X_API_KEY,
        },
      },
    )
    const data = await response.json()
    return JSON.parse(data ?? '{}')
  } catch (error) {
    console.log({ error })
    return notFound()
  }
}

const stripUnit = (value: number): number => {
  if (typeof value === 'number' && !isNaN(value) && !Number.isNaN(value)) {
    return value / (value * 0 + 1)
  }
  return value
}

/**
 * Convert pixel to em
 * @param value pixel value
 * @param fontSize base font size
 * @returns em value
 * @example
 *  const fontSize = em(32, 16); // => fontSize = '2em';
 */

export const em = (value: number | string, fontSize = 16): string => {
  if (isNaN(+value)) return value.toString()

  return `${stripUnit(+value) / stripUnit(fontSize)}em`
}

export const createTimeIntervals = (
  intervalMinutes = 30,
): IOption<string>[] => {
  const intervals: IOption<string>[] = []
  const startTime = moment().startOf('day').toDate()
  const endTime = moment().endOf('day').toDate()

  while (startTime <= endTime) {
    const value = moment(startTime).format('HH:mm')

    intervals.push({ label: value, value })

    startTime.setMinutes(startTime.getMinutes() + intervalMinutes)
  }

  return intervals
}

export function shallowEqual<T>(actual: T, expected: T): boolean {
  return deepEqual(actual, expected)
}

export const filterFees = (fee: IFee) => {
  // eslint-disable-next-line eqeqeq
  return fee.purpose == '7' && fee.includedInRate == 'false'
}

function mergeArray(
  oldArray: (TCoverage | TEquipment)[],
  newArray: (TCoverage | TEquipment)[],
) {
  const mergedArray: (TCoverage | TEquipment)[] = []
  oldArray = oldArray.map(_v => ({ ..._v, action: 'Cancel' }))
  oldArray.forEach(oldItem => {
    // @ts-ignore
    const newItemIndex = newArray.findIndex(item => item.type === oldItem.type)

    if (newItemIndex !== -1) {
      const newItem = newArray[newItemIndex]
      const mergedItem = {
        ...oldItem,
        ...newItem,
        action: newItem.action || ('Replace' as TExtraAction),
      }

      mergedArray.push(mergedItem)

      // Remove merged item from the new array
      newArray.splice(newItemIndex, 1)
    } else {
      // Item only exists in the old array
      mergedArray.push(oldItem)
    }
  })

  // Add items that only exist in the new array
  mergedArray.push(...newArray)

  return mergedArray
}

export const replaceExtras = (
  current?: TExtras,
  newExtras?: TExtras,
): TExtras | undefined => {
  const _newExtras = (newExtras ?? {}) as TExtras
  // @ts-ignore
  _newExtras['coverage'] = mergeArray(
    current?.coverage || [],
    // @ts-ignore
    newExtras?.coverage || [],
  )

  // @ts-ignore
  _newExtras['equipments'] = mergeArray(
    current?.equipments || [],
    // @ts-ignore
    newExtras?.equipments || [],
  )

  return _newExtras
}

export const joinArray = <T = Maybe<string>>(
  joinWith = ',',
  ...elements: T[]
): string => elements.filter(el => !!el).join(joinWith)

export const replacePhoneNumber = (number?: string) => {
  let _number = number
  if (!_number) return _number
  _number = _number.replace(/^00/, '+')

  if (!_number.includes('+')) return `+${_number}`
  return _number
}

export function getUniqueValues<T>(array: T[], property: keyof T) {
  return [...new Set(array.map(item => item[property]))]
}

export const extractExtraName = (
  extra: IPricedEquip | IPricedCoverage,
  t: (_key: DepthKeys, _options?: any) => string,
  isRussian: boolean,
) => {
  const description = isRussian
    ? // @ts-ignore
      extra?.equipment?.equipType || extra?.code
    : // @ts-ignore
      extra?.equipment?.description || extra?.details
  return joinArray(
    ' ',
    capitalize(
      // @ts-ignore
      isRussian ? t(`${description}Title`) : description,
    ),
  )
}

export const extractVehicleDetails = (
  charge: IVehicleCharge,
  t: (_key: DepthKeys, _options?: any) => string,
) => {
  const isRentalPeriod = charge.calculation.unitName === 'RentalPeriod'
  return joinArray(
    ' ',
    t(`vehicleCharge1`, {
      // @ts-ignore
      desc: t(`${camelCase(charge.description)}`),
      count: isRentalPeriod ? '/' : charge.calculation.quantity,
      // @ts-ignore
      unit: !isRentalPeriod
        ? // @ts-ignore
          t(`${charge.calculation.unitName}s`)
        : // @ts-ignore
          t(`${charge.calculation.unitName}`),
    }),
    !isRentalPeriod
      ? t(`vehicleCharge2`, {
          currency: charge.currencyCode,
          price: charge.calculation.unitCharge,
          // @ts-ignore
          unit: t(`${charge.calculation.unitName}`),
        })
      : undefined,
  )
}

export const humanFormatNumber = (
  numberStr: number | string,
  delimiter = ' ',
) => {
  // if (isNaN(numberStr)) return numberStr
  const intPart = `${numberStr}`.split('.')[0]
  const decimalPart = `${numberStr}`.split('.')[1]
  const splittedChars = intPart.split('')
  const symbol = ['-', '+'].includes(splittedChars?.[0])
    ? splittedChars?.[0]
    : ''
  const _intPart = symbol ? splittedChars.slice(1) : splittedChars
  const newIntPart = join(
    reverse(chunk(reverse(_intPart), 3).map(s => join(reverse(s), ''))),
    delimiter,
  )

  const newNumberStr = isNaN(+decimalPart)
    ? newIntPart
    : `${newIntPart}.${decimalPart}`

  return `${symbol}${newNumberStr}`
}

export const ellipsizeMiddle = (
  inputString: string,
  maxLength = 14,
  skip = false,
) => {
  if (inputString.length <= maxLength || skip) {
    return inputString
  }

  const halfLength = Math.floor((maxLength - 3) / 2)
  const leftHalf = inputString.slice(0, halfLength)
  const rightHalf = inputString.slice(inputString.length - halfLength)

  return leftHalf + '...' + rightHalf
}

export const generateMetaObject = (
  meta: IPageMeta,
  lang: TLang,
): Partial<Metadata> => {
console.log(process.env.GOOGLE_SITE_VERIFICATION, "verification")
      	return {
    icons: {
      icon: '/favicon.ico',
    },
    title: meta?.meta_title ?? 'Alamo',
    description: meta?.meta_description ?? '',
    alternates: {
      canonical: joinArray(
        '',
        process.env.NEXT_PUBLIC_CLIENT_PATH,
        lang === 'ru' ? `/${lang}` : undefined,
        meta.path === homePath ? undefined : meta.path,
      ),
      languages: [...APP_LANGUAGES, 'x-default'].reduce(
        (prev, lang) => ({
          ...prev,
          [lang]: joinArray(
            '',
            `${process.env.NEXT_PUBLIC_CLIENT_PATH}`,
            joinArray(
              '',
              ['en', 'x-default'].includes(lang) ? undefined : '/',
              ['en', 'x-default'].includes(lang) ? undefined : lang,
            ),
            meta.path === homePath ? undefined : meta.path,
          ),
        }),
        {},
      ),
    },
    verification: {
      google: process.env.GOOGLE_SITE_VERIFICATION,
    },
    openGraph: {
      type: 'website',
      title: meta?.meta_title ?? 'Alamo',
      siteName: meta?.meta_title ?? 'Alamo',
      description: meta?.meta_description ?? '',
      // images: meta?.meta_image
      //   ? [getImage(meta?.meta_image, false)]
      //   : undefined,
    },
    twitter: {
      card: 'summary_large_image',
      title: meta?.meta_title ?? 'Alamo',
      description: meta?.meta_description ?? '',
      // images: meta?.meta_image
      //   ? [getImage(meta?.meta_image, false)]
      //   : undefined,
    },
    keywords: meta.meta_keywords,
  }
}

export const getImage = (path?: string, isBackgroundImage = true) => {
  const imgPath = `${API_ENDPOINT}/storage${path}`
  if (!path) return ''
  if (!isBackgroundImage) return imgPath
  return `url("${imgPath}")`
}

export const openDays = (
  location: IBranch,
): { label: string; value: string }[] => {
  const weekDays = new Array(7).fill(1).map(function (_, i) {
    const weekDay = moment(i, 'e')
      .startOf('week')
      .isoWeekday(i + 1)
    return {
      label: weekDay.format('dddd'),
      value: weekDay.locale('en').format('dddd'),
    }
  })

  const openedDays: { label: string; value: string }[] = []

  const startsWithPrefix = (_: any, key: keyof IBranch) =>
    key.startsWith('Branch_Close') || key.startsWith('Branch_Open')
  const dates = _.pickBy(location, startsWithPrefix)
  weekDays.forEach(day => {
    const _day = day.value.slice(0, 3)
    const openedAt = dates[`Branch_Open_${_day}`]
    const closedAt = dates[`Branch_Close_${_day}`]
    const value = (
      closedAt === openedAt ? openedAt : joinArray(' - ', openedAt, closedAt)
    ) as string
    openedDays.push({
      label: capitalize(day.label),
      value,
    })
  })
  return openedDays
}

export const buildLanguageBasedSitemap = async (lang: TLang) => {
  // @ts-ignore
  const blogs = ((await getBlogPage(lang))?.data?.items ?? []).map(
    // @ts-ignore
    _blog => _blog.slug,
  )

  const paths = [
    ...appPaths[lang].map((path, idx) => ({
      lastModified: new Date().toISOString(),
      priority: !idx ? 1 : 0.8,
      changeFrequency: !idx ? 'yearly' : 'monthly',
      url: joinArray(
        '',
        process.env.NEXT_PUBLIC_CLIENT_PATH,
        lang === 'en' ? undefined : '/',
        lang === 'en' ? undefined : lang,
        lang === 'ru' && path === homePath ? undefined : path,
      ),
    })),
    // @ts-ignore
    ...blogs.map(_v => ({
      lastModified: new Date().toISOString(),
      changeFrequency: 'monthly',
      priority: 0.8,
      url: joinArray(
        '',
        process.env.NEXT_PUBLIC_CLIENT_PATH,
        lang === 'en' ? undefined : '/',
        lang === 'en' ? undefined : lang,
        '/',
        _v,
      ),
    })),
  ]

  return `<?xml version="1.0" encoding="UTF-8"?>
    <urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">
      ${paths
        .map(
          path => `
        <url>
          <loc>${path.url}</loc>
          <lastmod>${path.lastModified}</lastmod>
          <changefreq>${path.changeFrequency}</changefreq>
          <priority>${path.priority}</priority>
        </url>`,
        )
        .join('')}
    </urlset>`
}
